import Header from "../../components/ui/header";
import CollapsibleCard from "../../components/ui/accordion";
import { CopyButton } from "../../components/ui/copy_button";
import {
  absInvert,
  cleanNbsp,
  formatCurrency,
  formatCurrencyAbs,
  formatCurrencyAbsInt,
  formatCurrencyAbsInvert,
  formatCurrencyAbsNo
} from "../../components/ui/money";
import { useProfile } from "../../context/ProfileContext";
import { API } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";

type RecordType = Record<string, Record<string, any>>;

const allowedKeys: Record<string, string> = {
  // debt_amount: 'До сплати',
  // controller_calculation: 'Розрахунок',
  // controller_reading: 'Показання',
  // current_reading: 'Поточні',
  // previous_reading: 'Попередні',
  // reading_date: 'Дата',
  debt_amount: 'До сплати',
  controller_calculation: 'Розрахунок',
  controller_reading: 'Поточні',
  current_reading: 'Попередні',
  // previous_reading: 'Попередні',
  reading_date: 'Дата',
  // period: 'Період',
};

const allowedKeysSum: Record<string, string> = {
  controller_calculation: 'Розрахунок контролера',
  debt_amount: 'До сплати',
  reading_date: 'Дата',
  // period: 'Період',
};

const i18n: Record<string, string> = {
  day: 'День',
  night: 'Ніч',
  total: 'Разом',
};

type RenderRecordProps = {
  record: Record<string, any>;
  account: string;
  day: number;
  night: number;
  response: any;
  showSummary?: boolean;
  showHeaders?: boolean;
};

type RenderHistoryRecordProps = {
  records: RecordType;
  account: string;
  response: any;
};

const sumAmountOfDayNight = (sumAmountDay: number, sumAmountNight: number) => {
  return sumAmountDay + sumAmountNight;
}

const CounterRecord: React.FC<RenderRecordProps> = (
  {
    record,
    account,
    day,
    night,
    response,
    showSummary = false,
    showHeaders = true,
  }) => {
  const { profile, roles, token } = useProfile();

  // Initialize numeric summary
  let summaryAccount: any | null = {};
  const summary: Record<string, number | string> = {};

  const updateField = async (accountId: string, period: string, slot: string, field: string, value: any) => {
    if (!accountId) {
      window.alert("Не вдалося визначити особовий рахунок");
      return;
    }
    try {
      const res = await fetch(API.COUNTERS_UPDATE(accountId), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ period, slot, field, value }),
      });
      if (res.ok) {
        window.location.reload();
      } else {
        const data = await res.json().catch(() => ({}));
        window.alert(data.error || "Не вдалося зберегти");
      }
    } catch (err) {
      window.alert(`Помилка сервера ${err}`);
    }
  };

  const promptEdit = (accountId: string, period: string, slot: string, field: string, label: string, current: any) => () => {
    const input = window.prompt(`Нове значення (${label}):`, current === undefined || current === null ? "" : String(current));
    if (input === null) return;
    updateField(accountId, period, slot, field, input);
  };

  const EditButton: React.FC<{ accountId: string; period: string; slot: string; field: string; label: string; current: any }> = ({ accountId, period, slot, field, label, current }) => (
    <button
      type="button"
      className="ml-1 text-gray-400 hover:text-gray-700"
      onClick={promptEdit(accountId, period, slot, field, label, current)}
      title="Редагувати"
    >
      <FontAwesomeIcon icon={faEdit}/>
    </button>
  );

  const tAllowedKeys = (key: string) => {
    const translation = allowedKeys[key];
    return translation ? translation : key;
  }

  const tI18NKeys = (key: string) => {
    const translation = i18n[key];
    return translation ? translation : key;
  }

  // Prepare initial zero values
  Object.keys(allowedKeys).forEach((key) => {
    summary[key] = 0;
  });

  const isDate = (str: any) => /^\d{2}[-.]\d{2}[-.]\d{4}$/.test(str);

  summaryAccount = Object.values(record)?.[0] || {};
  // Aggregate values
  Object.values(record).forEach((mainValue) => {
    Object.keys(allowedKeysSum).forEach((key) => {
      const raw = mainValue[key];

      if (isDate(raw)) {
        summary[key] = raw;
        return;
      }

      // Try parsing numbers (with comma or dot as decimal separator)
      const normalized = typeof raw === 'string' ? raw.replace(',', '') : raw;
      const parsed = parseFloat(normalized);

      if (!isNaN(parsed)) {
        let num = summary[key] as number;
        summary[key] = num + parsed;
      } else {
        // Just keep the last seen non-numeric value
        summary[key] = raw;
      }
    });
  });

  let sumDayNight = 0;
  let sumAmountDay = 0;
  let sumAmountNight = 0;
  let sumDayNightCounters = 0;
  let sumDayNightOutput = (<></>);
  const isAdmin = roles?.[0] === "ADMIN";

  const aKey = 'current_reading';
  const bKey = 'controller_reading';
  const cKey = 'previous_reading';
  const dKey = 'debt_amount';

  const currentReadingDay = Array.isArray(record?.["day"]?.[aKey]) ? (record?.["day"][aKey]?.[0]) : (record?.["day"]?.[aKey]?.toString() || 0);
  const controllerReadingDay = Array.isArray(record?.["day"]?.[bKey]) ? (record?.["day"][bKey]?.[0]) : (record?.["day"]?.[bKey]?.toString() || 0);
  let amountDay = 0;
  const previousReadingDay = Array.isArray(record?.["day"]?.[cKey]) ? (record?.["day"][cKey]?.[0]) : (record?.["day"]?.[cKey]?.toString() || 0);
  if (controllerReadingDay === currentReadingDay) {
    amountDay = previousReadingDay - currentReadingDay;
  } else {
    amountDay = currentReadingDay - controllerReadingDay;
  }
  const controllerCalculationDay = record?.["day"]?.["controller_calculation"];
  const isControllerCalculationDayBlank = controllerCalculationDay === undefined || controllerCalculationDay === null || controllerCalculationDay === '';
  if (isControllerCalculationDayBlank && record?.["day"]?.["debt_amount_paid"]) {
    // Already settled (BAF/import cleared it) — don't show a readings-based estimate as if it's still owed.
    amountDay = 0;
  }
  const moneyDay = String(record?.["day"]?.[dKey] ? (record?.["day"]?.[dKey] || 0) : 0).replace(/,/g, '');
  const totalAmountDay = String(formatCurrency(Number(moneyDay))).replace(/ /g, ' ');

  const currentReadingNight = Array.isArray(record?.["night"]?.[aKey]) ? (record?.["night"][aKey]?.[0]) : (record?.["night"]?.[aKey]?.toString() || 0);
  const controllerReadingNight = Array.isArray(record?.["night"]?.[bKey]) ? (record?.["night"][bKey]?.[0]) : (record?.["night"]?.[bKey]?.toString() || 0);
  const previousReadingNight = Array.isArray(record?.["night"]?.[cKey]) ? (record?.["night"][cKey]?.[0]) : (record?.["night"]?.[cKey]?.toString() || 0);
  let amountNight = 0;
  if (controllerReadingNight === currentReadingNight) {
    amountNight = previousReadingNight - currentReadingNight;
  } else {
    amountNight = currentReadingNight - controllerReadingNight;
  }
  const controllerCalculationNight = record?.["night"]?.["controller_calculation"];
  const isControllerCalculationNightBlank = controllerCalculationNight === undefined || controllerCalculationNight === null || controllerCalculationNight === '';
  if (isControllerCalculationNightBlank && record?.["night"]?.["debt_amount_paid"]) {
    amountNight = 0;
  }
  const moneyNight = String(record?.["night"]?.[dKey] ? (record?.["night"]?.[dKey] || 0) : 0).replace(/,/g, '');
  const totalAmountNight = String(formatCurrency(Number(moneyNight))).replace(/ /g, ' ');

  const readingDay = controllerReadingDay === currentReadingDay ?
    `${previousReadingDay} - ${currentReadingDay} = ${amountDay}` :
    `${currentReadingDay} - ${controllerReadingDay} = ${amountDay}`;

  const readingNight = controllerReadingNight === currentReadingNight ?
    `${previousReadingNight} - ${currentReadingNight} = ${amountNight}` :
    `${currentReadingNight} - ${controllerReadingNight} = ${amountNight}`;

  const totalAmount = Number(moneyDay) + Number(moneyNight);

  const nightExists = !!record?.["night"];
  const isPeriodPaid = !!record?.["day"]?.["debt_amount_paid"] && (!nightExists || !!record?.["night"]?.["debt_amount_paid"]);

  return (
    <div className="space-y-6 px-1">
      <div className="text-sm overflow-x-auto">
        <table className="table-auto border border-gray-200 w-full">
          {showHeaders && (
            <thead>
            <tr>
              <th className="border border-gray-200 text-red-700" key={`header-${account}`}>Послуга</th>
              {Object.keys(allowedKeys).reverse().map((item: any) => (
                <th className="border border-gray-200 text-red-700" key={item}>{tAllowedKeys(item)}</th>
              ))}
            </tr>
            </thead>
          )}
          <tbody className={"text-sm text-gray-900"}>
          {Object.entries(record).map(([mainKey, mainValue], index) => {
            const periodKey = mainValue?.["period"]?.[0];
            const accountId = mainValue?.["accounts"]?.["prefix"] || summaryAccount?.account || summaryAccount?.accounts?.prefix || response?.account || response?.accounts?.["prefix"] || account;
            return (
              <>
                {mainKey === "day" && (
                  <tr
                    key={`tr-title-period-${mainKey}-${Math.random()}`}
                    className={""}
                  >
                    <td colSpan={Object.keys(allowedKeys).length + 1}
                        className={"text-right pr-2"}>
                      {mainValue?.["period"]?.[0]}
                      {isAdmin && (
                        <label className="ml-2 text-xs font-normal whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isPeriodPaid}
                            onChange={(e) => updateField(accountId, periodKey, "both", "debt_amount_paid", e.target.checked)}
                          /> Сплачено (приховати рахунок)
                        </label>
                      )}
                    </td>
                  </tr>
                )}
                <tr
                  key={`tr-title-${mainKey}-${Math.random()}`}
                  className={mainKey === "day" ? "bg-amber-50" : (mainKey === "night" ? "bg-indigo-50" : "")}
                >
                  <td className="text-sm border border-gray-200 w-36">
                    {tI18NKeys(mainKey)}
                  </td>
                  {Object.entries(allowedKeys).reverse().map(([key, label]) => {
                      const rawValue = mainValue?.[key];
                      let amount = Array.isArray(rawValue) ? (rawValue?.[0]) : (rawValue?.toString() || 0);
                      amount = String(amount).replace(/,/g, ''); // Ensure decimal point is used
                      const sumDayNightAmount = parseFloat(amount) || 0;
                      switch (key) {
                        case "controller_calculation": {
                          sumDayNightCounters += sumDayNightAmount;
                          const isBlank = rawValue === undefined || rawValue === null || rawValue === '';
                          const display = isBlank ? (mainKey === "day" ? amountDay : amountNight) : Number(amount);
                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">{display}
                              {isAdmin && <EditButton accountId={accountId} period={periodKey} slot={mainKey} field={key} label={label} current={rawValue}/>}
                            </td>
                          )
                        }
                        case "debt_amount":
                          if (mainKey === "day") {
                            sumAmountDay += sumDayNightAmount;
                          }
                          if (mainKey === "night") {
                            sumAmountNight += sumDayNightAmount;
                          }
                          if (mainKey === "night") {
                            sumDayNight = sumAmountOfDayNight(sumAmountDay, sumAmountNight);
                            sumDayNightOutput = (
                              <tr
                                key={`tr-title-${mainKey}-${Math.random()}`}
                                className={sumDayNight < 0 ? "bg-red-50" : ""}
                              >
                                <td colSpan={Object.keys(allowedKeys).length - 1}
                                    className="text-sm border border-gray-200 w-36 text-right pr-2">{tI18NKeys('total')}</td>
                                <td
                                  key={`td-${mainKey}-${key}-${Math.random()}`}
                                  className="border border-gray-200 w-36">{sumDayNightCounters || (amountDay + amountNight) || "-"}</td>
                                <td
                                  key={`td-${mainKey}-${key}-${Math.random()}`}
                                  className="border border-gray-200 w-36">{sumDayNight < 0 ? <>Переплата:<br/></> : ''}{formatCurrencyAbs(sumDayNight) || "-"}</td>
                              </tr>
                            )
                          }
                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">{formatCurrency(Number(amount))}
                              {isAdmin && <EditButton accountId={accountId} period={periodKey} slot={mainKey} field={key} label={label} current={rawValue}/>}
                            </td>
                          )
                        default:
                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">{amount}
                              {isAdmin && key !== "reading_date" && <EditButton accountId={accountId} period={periodKey} slot={mainKey} field={key} label={label} current={rawValue}/>}
                            </td>
                          )
                      }
                    }
                  )}
                </tr>
                {sumDayNightOutput}
              </>
            )
          })}
          </tbody>
        </table>
      </div>

      {/* Vertical Result Summary */}
      {showSummary && (
        <div
          className="border rounded-lg p-4 shadow-sm bg-white space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 space-y-2 text-sm text-gray-900 text-left">
            {isAdmin && (
              <div className="text-sm text-gray-900 text-left">
              <span
                className="font-bold"></span> {summaryAccount?.name || summaryAccount?.responsible_owner || response?.name || response?.responsible_owner || ''}
                <br/>
                <span
                  className="font-bold">О/р:</span> {summaryAccount?.account || summaryAccount?.accounts?.prefix || response?.account || response?.accounts?.["prefix"] || ''}
                <br/>
                Дата: {record?.["day"]?.reading_date}
                <br/>
                День: {readingDay} = {totalAmountDay}
                <br/>
                {record?.["night"] && (
                  <>
                    Ніч: {readingNight} = {totalAmountNight}
                  </>
                )}
                <br/>
                Разом: {formatCurrency(totalAmount)}
                <br/>
                {record?.["night"] ? (
                  <CopyButton
                    text={`Дата: ${record?.["day"]?.reading_date}\nДень: ${readingDay} = ${totalAmountDay}\nНіч: ${readingNight} = ${totalAmountNight}\nРазом: ${cleanNbsp(formatCurrency(totalAmount))}`}
                  />
                ) : (
                  <CopyButton
                    text={`Дата: ${record?.["day"]?.reading_date}\n${readingDay} = ${totalAmountDay}\nРазом: ${cleanNbsp(formatCurrency(totalAmount))}`}
                  />
                )}
              </div>
            )}
            <div>
              <div>
                Електроенергія
                від {summaryAccount?.name || summaryAccount?.responsible_owner || response?.name || response?.responsible_owner || profile?.name},
                о/р {summaryAccount?.account || summaryAccount?.accounts?.prefix || response?.account || response?.accounts?.["prefix"] || profile?.account}, {formatCurrencyAbsInt((day as number) == 0 ? Math.abs(amountDay) : (day as number))}/{formatCurrencyAbsInt((night as number) == 0 ? Math.abs(amountNight) : (night as number))}
                <br/>
                <CopyButton
                  text={`Електроенергія від ${summaryAccount?.name || summaryAccount?.responsible_owner || response?.name || response?.responsible_owner || profile?.name}, о/р ${summaryAccount?.account || summaryAccount?.accounts?.prefix || response?.account || response?.accounts?.["prefix"] || profile?.account}, ${formatCurrencyAbsInt((day as number) == 0 ? Math.abs(amountDay) : (day as number))}/${formatCurrencyAbsInt((night as number) == 0 ? Math.abs(amountNight) : (night as number))}`}
                />
              </div>
              <div>
                {formatCurrencyAbs((summary?.debt_amount || 0) as number)}
                <br/>
                <CopyButton text={`${cleanNbsp(formatCurrencyAbsNo((summary?.debt_amount || 0) as number))}`}/>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  )
};

const CounterRecordHistory: React.FC<RenderHistoryRecordProps> = (
  {
    records,
    account,
    response,
  }) => {
  // Initialize numeric summary
  const summary: Record<string, number | string> = {};

  const tAllowedKeys = (key: string) => {
    const translation = allowedKeys[key];
    return translation ? translation : key;
  }

  const tI18NKeys = (key: string) => {
    const translation = i18n[key];
    return translation ? translation : key;
  }

  // Prepare initial zero values
  Object.keys(allowedKeys).forEach((key) => {
    summary[key] = 0;
  });

  const sortedKeysC = Object.keys(records || {});
  sortedKeysC.sort((keyA, keyB) => {
    let partsA = String(String(keyA).split(" - ", 1)[0]).split(".");
    let partsB = String(String(keyB).split(" - ", 1)[0]).split(".");
    let sortA = new Date(parseInt(partsA[2]), parseInt(partsA[1]) - 1, parseInt(partsA[0]));
    let sortB = new Date(parseInt(partsB[2]), parseInt(partsB[1]) - 1, parseInt(partsB[0]));
    return sortA.getTime() - sortB.getTime()
  });
  // sortedKeysC.sort((keyA, keyB) => parseDate(keyA).getTime() - parseDate(keyB).getTime());
  sortedKeysC.reverse()

  return (
    <div className="space-y-6 px-1">
      <div className="text-sm overflow-x-auto">
        <table className="table-auto border border-gray-200 w-full">
          <thead>
          <tr>
            <th className="border border-gray-200 text-red-700" key={`header-${account}`}>Послуга</th>
            {Object.keys(allowedKeys).reverse().map((item: any) => (
              <th className="border border-gray-200 text-red-700" key={item}>{tAllowedKeys(item)}</th>
            ))}
          </tr>
          </thead>
          <tbody className={"text-sm text-gray-900"}>
          {sortedKeysC.map((dnMainKey) => {
            const dnMainValue = records[dnMainKey] || {};
            let sumDayNight = 0;
            let sumAmountDay = 0;
            let sumAmountNight = 0;
            let dayMoreZero = true
            let nightMoreZero = true
            let sumDayNightCounters = 0;
            let night = (<></>);
            return Object.entries(dnMainValue).map(([mainKey, mainValue], index) => {
              return (
                <>
                  {mainKey === "day" && (
                    <tr
                      key={`tr-title-period-${mainKey}-${Math.random()}`}
                      className={""}
                    >
                      <td colSpan={Object.keys(allowedKeys).length + 1}
                          className={"text-right pr-2"}>{mainValue?.["period"]?.[0]}</td>
                    </tr>
                  )}
                  <tr
                    key={`tr-title-${mainKey}-${Math.random()}`}
                    className={mainKey === "day" ? "bg-amber-50" : (mainKey === "night" ? "bg-indigo-50" : "")}
                  >
                    <td className="text-sm border border-gray-200 w-36">{tI18NKeys(mainKey)}</td>
                    {Object.entries(allowedKeys).reverse().map(([key, label]) => {
                      let amount = Array.isArray(mainValue?.[key]) ? (mainValue[key]?.[0]) : (mainValue?.[key]?.toString() || 0);
                      amount = String(amount).replace(/,/g, ''); // Ensure decimal point is used
                      const sumDayNightAmount = parseFloat(amount) || 0;
                      switch (key) {
                        case "controller_calculation":
                          if (isNaN(amount)) {
                            const aKey = 'current_reading';
                            const currentReading = Array.isArray(mainValue?.[aKey]) ? (mainValue[aKey]?.[0]) : (mainValue?.[aKey]?.toString() || 0);
                            const bKey = 'controller_reading';
                            const controllerReading = Array.isArray(mainValue?.[bKey]) ? (mainValue[bKey]?.[0]) : (mainValue?.[bKey]?.toString() || 0);
                            if (controllerReading === currentReading) {
                              const cKey = 'previous_reading';
                              const previousReading = Array.isArray(mainValue?.[cKey]) ? (mainValue[cKey]?.[0]) : (mainValue?.[cKey]?.toString() || 0);
                              amount = controllerReading - previousReading;
                            } else {
                              amount = controllerReading - currentReading;
                            }
                          }

                          if (!isNaN(amount) && amount !== "") {
                            sumDayNightCounters += sumDayNightAmount;
                            return (
                              <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                  className="border border-gray-200 w-36">{absInvert(amount) || "-"}</td>
                            )
                          }

                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">{absInvert(amount) || "-"}</td>
                          )
                        case "debt_amount":
                          if (!isNaN(amount) && amount !== "") {
                            if (mainKey === "day") {
                              sumAmountDay += sumDayNightAmount;
                              dayMoreZero = false;
                            }
                            if (mainKey === "night") {
                              sumAmountNight += sumDayNightAmount;
                              nightMoreZero = false;
                            }
                            if (mainKey === "night") {
                              sumDayNight = sumAmountOfDayNight(sumAmountDay, sumAmountNight);
                              night = (
                                <tr
                                  key={`tr-title-${mainKey}-${Math.random()}`}
                                  className={""}
                                >
                                  <td colSpan={Object.keys(allowedKeys).length - 1}
                                      className="text-sm border border-gray-200 w-36 text-right pr-2">{tI18NKeys('total')}</td>
                                  <td
                                    key={`td-${mainKey}-${key}-${Math.random()}`}
                                    className="border border-gray-200 w-36">{absInvert(sumDayNightCounters) || "-"}</td>
                                  <td
                                    key={`td-${mainKey}-${key}-${Math.random()}`}
                                    className="border border-gray-200 w-36">{formatCurrencyAbsInvert(sumDayNight) || "-"}</td>
                                </tr>
                              )
                            }
                            return (
                              <>
                                <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                    className="border border-gray-200 w-36">{formatCurrencyAbsInvert(amount) || "-"}</td>
                              </>
                            )
                          }
                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">-</td>
                          )
                        default:
                          return (
                            <td key={`td-${mainKey}-${key}-${Math.random()}`}
                                className="border border-gray-200 w-36">{amount}</td>
                          )
                      }
                    })}
                  </tr>
                  {night}
                </>
              )
            })
          })}
          </tbody>
        </table>
      </div>
    </div>
  )
};

export const CountersViewer = ({ record, section, account, response }: {
  record: RecordType,
  section: string,
  account: string,
  response: any,
}) => {

  const sortedKeysC = Object.keys(record || {});
  sortedKeysC.sort((keyA, keyB) => {
    let partsA = String(String(keyA).split(" - ", 1)[0]).split(".");
    let partsB = String(String(keyB).split(" - ", 1)[0]).split(".");
    let sortA = new Date(parseInt(partsA[2]), parseInt(partsA[1]) - 1, parseInt(partsA[0]));
    let sortB = new Date(parseInt(partsB[2]), parseInt(partsB[1]) - 1, parseInt(partsB[0]));
    return sortA.getTime() - sortB.getTime()
  });

  const periodKeyOne = sortedKeysC.reverse()[0];
  const periodValueOne = record?.[periodKeyOne] || {};
  let day = periodValueOne?.["day"]?.controller_calculation ? (periodValueOne?.["day"]?.controller_calculation !== "" ? periodValueOne?.["day"]?.controller_calculation : '0') : '0'
  let night = periodValueOne?.["night"]?.controller_calculation ? (periodValueOne?.["night"]?.controller_calculation !== "" ? periodValueOne?.["night"]?.controller_calculation : '0') : '0'
  if (day === "") {
    day = 0
  }
  if (night === "") {
    night = 0
  }

  return (
    <>
      <Header className={""} key={`header-${periodKeyOne}-${Math.random()}`}>
        <CollapsibleCard key={`${periodKeyOne}-${Math.random()}`} title={periodKeyOne} open={true} more={false}>
          <CounterRecord
            key={`RenderRecord-${periodKeyOne}-${Math.random()}`}
            record={periodValueOne}
            account={account}
            day={day}
            night={night}
            showSummary={true}
            showHeaders={true}
            response={response}
          />
        </CollapsibleCard>
      </Header>
      {/*<Header className={""} key={`header-2-${Math.random()}`}>*/}
      {/*  <CollapsibleCard key={`2-1-${Math.random()}`} title={"Всі записи"} open={false}>*/}
      {/*    <CounterRecordHistory*/}
      {/*      key={`RenderRecordHistory-${Math.random()}`}*/}
      {/*      records={record}*/}
      {/*      account={account}*/}
      {/*      response={response}*/}
      {/*    />*/}
      {/*  </CollapsibleCard>*/}
      {/*</Header>*/}
    </>
  );
};
